import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, Check, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRuleCategories,
  searchRules,
  bulkUpdateRules,
  clearRules,
} from "../reducers/rulesInReviewSlice";

const FullRulesManager = () => {
  const dispatch = useDispatch();

  const access_token = useSelector((state) => state.authen.accessToken);
  const loggedInAdmin = useSelector((state) => state.authen.loggedInAdmin);

  const { categories, rules, loadingCategories, loadingRules, updating } =
    useSelector((state) => state.rulesInReview);

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [snackbar, setSnackbar] = useState(null);

  // organization lookup map: email -> organization_name
  const organizationLookup = useMemo(() => {
    if (!loggedInAdmin || !loggedInAdmin.data) return {};

    const map = {};
    loggedInAdmin.data.forEach((item) => {
      map[item.email] = item.organization_name;
    });

    return map;
  }, [loggedInAdmin]);

  // organization autocomplete options
  const organizationOptions = useMemo(() => {
    if (!loggedInAdmin || !loggedInAdmin.data) return [];

    return [...loggedInAdmin.data]
      .sort((a, b) =>
        (a.organization_name || "").localeCompare(b.organization_name || ""),
      )
      .map((item) => ({
        label: item.organization_name,
        email: item.email,
      }));
  }, [loggedInAdmin]);

  useEffect(() => {
    if (access_token) {
      dispatch(fetchRuleCategories({ access_token }));
    }
  }, [access_token, dispatch]);

  const handleFind = () => {
    if (!selectedCategory) {
      setSnackbar({ type: "error", msg: "Category must be selected" });
      return;
    }

    dispatch(
      searchRules({
        category: selectedCategory,
        license_email: selectedOrg?.email || "",
        access_token,
      }),
    );
  };

  const handleBulkUpdate = (value) => {
    if (!selectedCategory) {
      setSnackbar({ type: "error", msg: "Category must be selected" });
      return;
    }

    dispatch(
      bulkUpdateRules({
        category: selectedCategory,
        license_email: selectedOrg?.email || "",
        inreview: value,
        access_token,
      }),
    ).then(() => {
      setSnackbar({
        type: "success",
        msg: `Rules updated to ${value}`,
      });

      handleFind();
    });
  };

  const handleClear = () => {
    setSelectedOrg(null);
    setSelectedCategory(null);
    dispatch(clearRules());
  };

  const columns = [
    {
      field: "organization_name",
      headerName: "Organization",
      flex: 2,
      renderCell: (params) => {
        const email = params.row.license_email;
        return organizationLookup[email] || "";
      },
    },
    {
      field: "license_email",
      headerName: "License Email",
      flex: 2,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 2,
    },
    {
      field: "inreview",
      headerName: "In Review",
      flex: 1,
      renderCell: (params) =>
        params.value === "Enabled" ? (
          <Typography color="green" sx={{ pt: 1.5 }}>
            Enabled
          </Typography>
        ) : (
          <Typography color="red" sx={{ pt: 1.5 }}>
            Disabled
          </Typography>
        ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ mb: 3, fontWeight: "bold", color: "primary.main" }}
        >
          Global Category Enable/Disable
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Autocomplete
            size="small"
            options={organizationOptions}
            getOptionLabel={(option) => option.label || ""}
            filterOptions={(options, state) => {
              const input = (state.inputValue || "").toLowerCase();

              return options.filter((option) =>
                (option.label || "").toLowerCase().includes(input),
              );
            }}
            value={selectedOrg}
            onChange={(event, newValue) => {
              setSelectedOrg(newValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label="All Organizations" />
            )}
            sx={{ minWidth: 280 }}
          />
          <Autocomplete
            size="small"
            options={categories}
            value={selectedCategory}
            onChange={(e, val) => setSelectedCategory(val)}
            loading={loadingCategories}
            renderInput={(params) => (
              <TextField {...params} label="Category *" />
            )}
            sx={{ minWidth: 320 }}
          />

          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleFind}
            disabled={loadingRules}
          >
            Find
          </Button>

          <Button
            variant="contained"
            color="success"
            startIcon={<Check />}
            onClick={() => handleBulkUpdate("Enabled")}
            disabled={updating}
          >
            Enable
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={<X />}
            onClick={() => handleBulkUpdate("Disabled")}
            disabled={updating}
          >
            Disable
          </Button>

          <Button variant="outlined" onClick={handleClear}>
            Clear
          </Button>
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Results ({rules.length})
        </Typography>

        {loadingRules ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={rules}
            columns={columns}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
          />
        )}
      </Paper>

      {snackbar && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={() => setSnackbar(null)}
        >
          <Alert severity={snackbar.type}>{snackbar.msg}</Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default FullRulesManager;
